import React, {
    createContext, ReactChild, useEffect, useMemo, useState,
} from 'react'

import consoleService from '@src/services/consoleService'
import Keycloak from 'keycloak-js'
import AccessDeniedPage from '@src/pages/AccessDenied'
import {
    getUserInfo, initialExtendedProfile, initialUserInfo,
} from './lib'
import {
    ExtendedProfile, IAuthContext, KeycloakProfile, TKeycloak, UserInfo,
} from './dataTypes'

const {
    REACT_APP_AUTH_SERVER_URL: url, REACT_APP_REALM: realm, REACT_APP_RESOURCE: clientId,
} = process.env

const TIME_IN_MS = {
    second: 1000,
    minute: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
}

type Props = {
    children: ReactChild
}

export const AuthContext = createContext<IAuthContext>({
    userInfo: initialUserInfo,
    token: '',
    logout: () => {
        return null
    },
})

function AuthProvider({
    children,
}: Props) {
    const [
        token,
        setToken,
    ] = useState<string>('')
    const [
        timeOutId,
        setTimeOutId,
    ] = useState<NodeJS.Timeout | null>(null)
    const [
        keycloak,
        setKeycloak,
    ] = useState<TKeycloak | null>(null)
    const [
        status,
        setStatus,
    ] = useState<string>('INIT')
    const [
        extendedProfile,
        setExtendedProfile,
    ] = useState<ExtendedProfile>(initialExtendedProfile)

    useEffect(() => {
        (async () => {
            try {
                setStatus('PENDING')

                const lKeycloack: TKeycloak = new Keycloak({
                    url: url || '',
                    realm: realm || '',
                    clientId: clientId || '',
                }) as unknown as TKeycloak

                if (lKeycloack) {
                    await lKeycloack.init({
                        onLoad: 'login-required',
                    })

                    setKeycloak(lKeycloack)
                    const {
                        token: lToken,
                    } = lKeycloack

                    setToken(lToken)
                    setStatus('SUCCESS')
                } else {
                    setStatus('FAILURE')
                }
            } catch (error) {
                setStatus('FAILURE')
            }
        })()
    }, [])

    useEffect(() => {
        if (keycloak) {
            const {
                tokenParsed,
            } = keycloak
            const {
                exp,
            } = tokenParsed
            const timeout = exp * TIME_IN_MS.second - Date.now()

            if (timeOutId) {
                clearTimeout(timeOutId)
            }

            const newTimeOutId = setTimeout(() => {
                keycloak
                    .updateToken(TIME_IN_MS.minute * 5)
                    .then((refreshed) => {
                        if (refreshed) {
                            const {
                                token: lToken,
                            } = keycloak

                            setToken(lToken)
                        }
                    })
                    .catch((err) => {
                        consoleService.log(err)
                        keycloak.logout()
                    })
            }, timeout)

            setTimeOutId(newTimeOutId)
        }
        // Warning!!! timeOutId - don't put into deps - infinite refresh
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        keycloak,
        token,
    ])

    useEffect(() => {
        if (keycloak && !extendedProfile.email) {
            (async () => {
                try {
                    const profile: KeycloakProfile = await keycloak.loadUserProfile()
                    const {
                        tokenParsed,
                    } = keycloak
                    const {
                        groups: assignedRoles = [], name,
                    } = tokenParsed

                    setExtendedProfile({
                        ...profile,
                        assignedRoles,
                        name,
                    })
                } catch (error) {
                    setExtendedProfile(initialExtendedProfile)
                }
            })()
        }
    }, [
        extendedProfile.email,
        keycloak,
    ])

    const userInfo: UserInfo = useMemo(() => {
        if (!extendedProfile.email) {
            return initialUserInfo
        }

        return getUserInfo(extendedProfile, '')
    }, [extendedProfile])

    // eslint-disable-next-line no-nested-ternary
    return status === 'SUCCESS' ? (
        <AuthContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            value={{
                userInfo,
                token,
                logout: keycloak
                    ? keycloak.logout
                    : () => {
                        return null
                    },
            }}
        >
            {children}
        </AuthContext.Provider>
    ) : status === 'FAILURE' ? (
        <AccessDeniedPage />
    ) : null
}

export default AuthProvider
